/**
 * Unified theme
 *
 * The single source for theme classes (CLAUDE.md §3). Import it and use
 * `className={theme.text.primary}`.
 *
 * Decision: ADR-057 — colour carries QC status and nothing else.
 * See docs/decisions/ADR-057-colour-means-status.md
 *
 * What changed and why it matters:
 *
 * This used to concatenate two hard-coded palettes — `lightTheme` said
 * `text-gray-900`, `darkTheme` said `dark:text-slate-50`, and theme.ts glued
 * them into `"text-gray-900 dark:text-slate-50"`. That made a third styling
 * system alongside the CSS custom properties in index.css and the raw
 * `text-slate-400` scattered through components, so a contrast fix applied to
 * the tokens reached only the third of the app that used them.
 *
 * Every value below is now a semantic token class. The tokens are already
 * dark-aware — index.css redefines the custom properties under `.dark` — so
 * there are no `dark:` variants here at all, and there is exactly one place to
 * change a colour. That is the point: the 900-odd call sites did not move, but
 * they now resolve through the same tokens as everything else.
 *
 * Contrast for each token is recorded in index.css and enforced by
 * src/tests/TokenContrast.test.tsx.
 */

export const theme = {
  // Surfaces. `primary` is the page ground; `secondary` is a card sitting on it.
  background: {
    primary: 'bg-bg-base',
    secondary: 'bg-bg-surface',
    tertiary: 'bg-bg-subtle',
    elevated: 'bg-bg-elevated',
  },

  text: {
    primary: 'text-text-primary',
    secondary: 'text-text-secondary',
    tertiary: 'text-text-tertiary',
    disabled: 'text-interactive-disabled',
    inverse: 'text-text-inverse',
  },

  // `primary` is a decorative divider; `secondary` bounds a control and clears
  // 3:1 (WCAG 1.4.11). Use `secondary` on anything interactive.
  border: {
    primary: 'border-border',
    secondary: 'border-border-strong',
    light: 'border-border-subtle',
    focus: 'border-interactive-primary',
  },

  interactive: {
    primary: {
      base: 'bg-interactive-primary text-white',
      hover: 'hover:bg-interactive-primary-hover',
      active: 'active:bg-interactive-primary-hover',
      disabled: 'bg-interactive-disabled text-white cursor-not-allowed',
    },
    secondary: {
      base: 'bg-bg-subtle text-text-primary border border-border-strong',
      hover: 'hover:bg-bg-hover',
      active: 'active:bg-bg-hover',
      disabled: 'bg-bg-subtle text-interactive-disabled cursor-not-allowed',
    },
    link: {
      base: 'text-interactive-primary underline underline-offset-2',
      hover: 'hover:text-interactive-primary-hover',
      // A separate visited colour was purple, which under ADR-057 would be a
      // second meaning for a hue that carries none. Links read the same
      // before and after visiting.
      visited: 'text-interactive-primary',
    },
  },

  // The only colours in the system that mean something. Each `text` value is
  // >= 4.5:1 on its own surface, so status is legible as text and not just as
  // a fill behind it.
  status: {
    success: {
      background: 'qc-success-bg',
      border: 'border-qc-success',
      text: 'qc-success',
      icon: 'qc-success',
    },
    warning: {
      background: 'qc-warning-bg',
      border: 'border-qc-warning',
      text: 'qc-warning',
      icon: 'qc-warning',
    },
    error: {
      background: 'qc-error-bg',
      border: 'border-qc-error',
      text: 'qc-error',
      icon: 'qc-error',
    },
    info: {
      background: 'qc-info-bg',
      border: 'border-qc-info',
      text: 'qc-info',
      icon: 'qc-info',
    },
  },

  // Navigation is chrome, so it is neutral. The active item is marked by
  // weight, a filled ground and a leading rule — not by hue alone
  // (accessibility.md › Vision: "Convey information with more than color alone").
  navigation: {
    item: {
      base: 'text-text-secondary',
      hover: 'hover:bg-bg-hover hover:text-text-primary',
      active: 'bg-bg-subtle text-text-primary font-medium',
    },
    background: 'bg-bg-surface',
    border: 'border-border',
  },

  // Cards in a dense instrument sit on the page, they do not float above it.
  card: {
    background: 'bg-bg-surface',
    border: 'border-border',
    shadow: 'shadow-sm',
    hover: 'hover:shadow-md',
  },

  form: {
    input: {
      background: 'bg-bg-surface',
      border: 'border-border-strong',
      focus: 'focus:border-interactive-primary',
      disabled: 'bg-bg-subtle border-border text-interactive-disabled',
      error: 'border-qc-error focus:border-qc-error',
    },
    label: 'text-text-primary',
    helperText: 'text-text-secondary',
    errorText: 'qc-error',
  },

  /**
   * Feature accents.
   *
   * These were `bg-blue-500` / `bg-green-500` / `bg-purple-500` /
   * `bg-orange-500` — four categorical hues on navigation tiles. Under
   * ADR-057 categories are distinguished by name and icon, and the colour
   * budget is reserved for pass / review / fail, so a green "process" tile
   * cannot be misread as a passing part. All four are now the same neutral.
   */
  features: {
    upload: 'bg-bg-subtle text-text-primary',
    process: 'bg-bg-subtle text-text-primary',
    analytics: 'bg-bg-subtle text-text-primary',
    utilities: 'bg-bg-subtle text-text-primary',
  },
} as const;

/**
 * Combine theme classes with custom ones, dropping falsy entries.
 */
export const cn = (...classes: (string | undefined | false)[]) => {
  return classes.filter(Boolean).join(' ');
};
